import { type Html, html, UnsafeHtml, View } from 'rune-ts';
import klass from './CancelableAlert.module.scss';
import { shareFont } from '@services/MarppleShop/shared/typography';
import { ButtonAction } from '@services/MarppleShop/shared/components/atoms/ButtonAction/ButtonAction.ts';
import {
  Modal,
  ModalClosedEvent,
  ModalProps,
} from '@services/MarppleShop/renderApp/components/atoms/Modal/Modal.ts';

export interface ConfirmProps {
  backdrop?: boolean;
  backdrop_not_close?: boolean;
  appendTo?: string | Element;
  title?: string;
  message?: string | Html | UnsafeHtml;
  confirmText?: string;
  cancelText?: string;
  size?: ModalProps['size'];
  whiteSpace?: 'pre_line' | 'pre_wrap' | 'initial';
}

/**
 * 기존 Alert 클래스의 경우 esc 입력, 백그라운드 클릭 등
 * 얼럿 내부 CTA 버튼 클릭 외 얼럿 닫기 동작 시 open 메서드에서 반환하는 Promise가
 * 무한히 resolve 되지 않는 버그가 있어 본 클래스를 추가합니다.
 *
 * @see https://github.com/marpple/marpple-web/blob/6cde4dd6fd0dc336ee05f8e95b53c6c457ba6002/services/MarppleShop/renderApp/components/cells/Confirm/Confirm.ts#L1-L114
 */
export class CancelableAlert extends View<ConfirmProps> {
  override template(data: ConfirmProps) {
    const { size = 'free', whiteSpace = 'initial' } = data;
    const whiteSpaceStyle = {
      pre_line: klass.pre_line,
      pre_wrap: klass.pre_wrap,
      initial: klass.initial,
    };
    const message_style = `${klass.message} ${whiteSpaceStyle[whiteSpace]} ${shareFont('14_medium')}`;
    return html`
      <div>
        ${new Modal({
          size,
          isOpen: false,
          backdrop: data.backdrop,
          backdrop_not_close: data.backdrop_not_close,
          body: html`
            <div class="${klass.alert}">
              <div class="${klass.text}">
                <div class="${klass.title} ${shareFont('20_bold')}">${data.title}</div>
                <div class="${message_style}">${data.message ?? ''}</div>
              </div>
              <div class="${klass.buttons}">
                ${new ButtonAction({
                  klass: `${klass.button} confirm_btn`,
                  label: data.confirmText || ET('mps2::common::confirm'),
                  type: 'blue',
                  size: 'large',
                })}
              </div>
            </div>
          `,
        })}
      </div>
    `;
  }

  getModal() {
    const modal = this.subViewsFromTemplate[0] as Modal;
    return modal;
  }

  protected async show() {
    if (this.data.appendTo) {
      let target = this.data.appendTo as Element | null;
      if (typeof this.data.appendTo === 'string') {
        target = document.body.querySelector(this.data.appendTo);
      }
      if (target) target.appendChild(this.render());
    } else {
      document.body.appendChild(this.render());
    }

    this.getModal().open();
    this.element().focus();

    return new Promise((res, rej) => {
      const confirm_btn = this.element()?.querySelector('.confirm_btn');
      const cancel_btn = this.element()?.querySelector('.cancel_btn');
      const handleOK = () => {
        detach();
        this.close();
        res(true);
      };
      const handleCancel = () => {
        detach();
        this.close();
        res(false);
      };
      const detach = () => {
        confirm_btn?.removeEventListener('click', handleOK);
        cancel_btn?.removeEventListener('click', handleCancel);
        this.removeEventListener(ModalClosedEvent, handleCancel);
      };

      confirm_btn?.addEventListener('click', handleOK);
      cancel_btn?.addEventListener('click', handleCancel);
      this.addEventListener(ModalClosedEvent, handleCancel);
    });
  }

  close() {
    this.getModal().close();
  }

  static async open(props: ConfirmProps) {
    const confirm = new CancelableAlert(props);
    return confirm.show();
  }
}
